import { BaseTags, TBaseTagsProps } from 'src/components/BaseTags';
import { EChartTooltips, getChartTooltip } from 'src/constants/chartTooltips';
import { NET_ENERGY_KEY, SWITCHER_ICON_SIZES } from 'src/constants/application';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  TBarDataset,
  TChartEnergyTradeProfileProps,
  TLegendExtraProps,
  TLineDataset,
} from './ChartEnergyTradeProfile.types';

import { AxisXCustom } from 'src/components/_charts/AxisXCustom/AxisXCustom';
import { BaseSwitch } from 'src/components/BaseSwitch';
import { Chart } from 'chart.js';
import { ChartDataLoadingWrapper } from 'src/components/ChartDataLoadingWrapper';
import { ChartWrapper } from 'src/components/ChartWrapper';
import { EChartName } from 'src/components/_charts/chartsData';
import { UTCMoment } from 'src/utils/UTCMoment';
import { formatter } from 'src/utils/formatter';
import { getColor } from 'src/components/_charts/ChartEnergyTradeProfile/helpers';
import { selectSettingsData } from 'src/redux/configuration/configuration.selectors';
import { useChartJS } from 'src/hooks/useChartJS';
import { useSelector } from 'react-redux';
import vars from 'src/assets/styles/utils/vars.module.scss';

export const peakAnalysisConfig = [
  {
    property: 'baseline_peak_energy_kWh',
    borderColor: '#573AFF',
  },
  {
    property: 'peak_energy_net_kWh',
    borderColor: '#9B6BFD',
  },
  {
    property: 'capacity_kWh',
    borderColor: '#46F4FF',
    borderDash: [10, 10] as [number, number],
  },
];

export const ChartEnergyTradeProfile: React.FC<TChartEnergyTradeProfileProps> = ({
  showPeakAnalysis,
  showNetEnergy,
  legend,
  netEnergyData,
  importData,
  exportData,
  soldEnergy,
  boughtEnergy,
  togglePeakAnalysis,
  toggleIsolated,
  toggleNetEnergy,
  hasExternalTrades,

  startUnix,
  currentMarket,
  assetUuid,
  hasData,
}) => {
  const chartRef = useRef<Chart | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { currency } = useSelector(selectSettingsData);

  const handleLegendItemClick: TBaseTagsProps<TLegendExtraProps>['onTagClick'] = (item) => {
    if (item.value === NET_ENERGY_KEY) {
      toggleNetEnergy();
    } else {
      toggleIsolated(item.value);
    }
  };

  const peakAnalysisDatasets = useMemo(() => {
    const datasets: TLineDataset[] = [];
    const capacityDatasets: TLineDataset[] = [];

    if (!startUnix || !currentMarket) return [];

    [importData, exportData].forEach((data, index) => {
      peakAnalysisConfig.forEach(({ property, borderColor, borderDash }) => {
        const value = data[property];

        if (!value || Number.isNaN(value)) return;

        const container = property.includes('capacity') ? capacityDatasets : datasets;
        const isImport = index === 0;
        const finalValue = isImport ? value : -value;
        const datasetData: TLineDataset['data'] = [];

        for (let unix = startUnix; unix <= currentMarket; unix += 15 * 60) {
          datasetData.push({ x: UTCMoment.fromUnix(unix).valueOf(), y: finalValue });
        }

        const dataset: TLineDataset = {
          type: 'line',
          data: datasetData,
          label: JSON.stringify({
            title: `Current peak ${isImport ? 'imports' : 'exports'}`,
            unit: 'kWh',
          }),
          borderColor,
          borderDash,
          pointHoverRadius: 0,
          hidden: !showPeakAnalysis,
        };

        container.push(dataset);
      });
    });

    return [...datasets, ...capacityDatasets];
  }, [currentMarket, exportData, importData, showPeakAnalysis, startUnix]);

  const tradesDatasets: TBarDataset[] = useMemo(() => {
    const output: TBarDataset[] = [];

    if (!soldEnergy || !boughtEnergy || !assetUuid) return output;
    legend.forEach(({ value: assetName }) => {
      [soldEnergy[assetName], boughtEnergy[assetName]].forEach((data) => {
        const color = getColor(assetName, assetUuid);

        if (!data) return;

        output.push({
          type: 'bar',
          data: data,
          label: JSON.stringify({
            title: assetName,
            unit: 'kWh',
          }),
          borderColor: color,
          backgroundColor: color,
          hoverBackgroundColor: color,
        });
      });
    });

    return output;
  }, [legend, soldEnergy, boughtEnergy, assetUuid]);

  const datasets: Array<TLineDataset | TBarDataset> = useMemo(() => {
    const allDatasets = [
      {
        type: 'bar' as const,
        data: [...netEnergyData], // Create a new array reference because below we push data to it
        backgroundColor: vars['color-net-energy'],
        hoverBackgroundColor: vars['color-net-energy'],
        hidden: !showNetEnergy,
        label: JSON.stringify({
          title: 'Net Energy',
          unit: 'kWh',
        }),
      },
      ...tradesDatasets.map((item) => ({
        ...item,
        data: [...item.data],
        hidden: showNetEnergy,
      })),
      ...peakAnalysisDatasets.map((item) => ({
        ...item,
        data: [...item.data],
        hidden: !showPeakAnalysis,
      })),
    ];

    const mostItemsDataset = allDatasets.sort((a, b) => b.data.length - a.data.length)[0];

    allDatasets.forEach((dataset) => {
      for (let i = 0; i < mostItemsDataset.data.length; i++) {
        const x = mostItemsDataset.data[i].x;
        const timeValueExists = dataset.data.find((item) => item.x === x);
        if (!timeValueExists) {
          dataset.data.push({
            y: 0,
            x,
          });
        }
      }
    });

    return allDatasets;
  }, [netEnergyData, showNetEnergy, tradesDatasets, peakAnalysisDatasets, showPeakAnalysis]);

  useChartJS(
    EChartName.EnergyTradeProfile,
    canvasRef,
    chartRef,
    {
      datasets,
    },
    {
      datasetsLengthWillChange: true,
    },
  );

  useEffect(() => {
    if (chartRef.current && currentMarket) {
      chartRef.current.options.scales!.x!.max = currentMarket * 1000;
      chartRef.current.update();
    }
  }, [currentMarket]);

  return (
    <ChartWrapper
      title="Trade Profile"
      info={getChartTooltip(
        EChartTooltips.TOOLTIP_ENERGY_TRADE_PROFILE,
        formatter.getCurrencySymbol(currency),
      )}
      titleRightSlotComponent={
        <BaseSwitch
          name="peak-analysis"
          label="Peak Analysis"
          options={[
            { icon: 'close', iconSize: SWITCHER_ICON_SIZES.close, value: false },
            { icon: 'check-mark', iconSize: SWITCHER_ICON_SIZES.tick, value: true },
          ]}
          theme="gray-custom"
          variant="horizontal-small"
          disabled={!hasExternalTrades}
          value={showPeakAnalysis}
          onChange={togglePeakAnalysis}
        />
      }
      footerComponent={
        <BaseTags items={legend} name="legend" onTagClick={handleLegendItemClick} />
      }>
      <ChartDataLoadingWrapper loading={!hasData}>
        <canvas ref={canvasRef} />

        <AxisXCustom startUnix={startUnix} endUnix={currentMarket} />
      </ChartDataLoadingWrapper>
    </ChartWrapper>
  );
};
